import { useState, useEffect, useRef, useCallback } from 'react';
import nodeinstance from '../Global/nodeAxios';

// Optimized cache with better memory management and performance
const apiCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100; // Increased for better performance

const useServerPagination = ({
  apiEndpoint,
  token,
  defaultPageSize = 10,
  searchTerm = '',
  searchDebounceMs = 300,
  trigger = null, // Add trigger support for cache invalidation
  skipInitialLoad = false, // ADD: option to skip initial load
}) => {
  // Refs for cleanup and preventing duplicate calls
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const lastRequestRef = useRef(null);
  const initialLoadRef = useRef(false);
  // Tracks the page of the most recently *dispatched* request (not yet resolved)
  // Used to drop stale responses when the user clicks fast
  const pendingPageRef = useRef(null);
  const isFetchingRef = useRef(false);
  
  // Refs for debounced search to avoid stale closures
  const currentPageSizeRef = useRef(defaultPageSize);
  const currentPageRef = useRef(1);
  const searchTermRef = useRef(searchTerm);
  const prevTriggerRef = useRef(trigger);
  const prevApiEndpointRef = useRef(apiEndpoint);
  const totalPagesRef = useRef(1); // ADD: avoid stale closure in fetchData

  // State management
  const [data, setData] = useState([]);
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    pageSize: defaultPageSize,
    totalPages: 1,
    totalRecords: 0,
    isLoading: false,
    error: null
  });

  // Update refs when state changes
  useEffect(() => {
    currentPageSizeRef.current = paginationState.pageSize;
    currentPageRef.current = paginationState.currentPage;
    searchTermRef.current = searchTerm;
    totalPagesRef.current = paginationState.totalPages; // keep ref in sync
  }, [paginationState.pageSize, paginationState.currentPage, paginationState.totalPages, searchTerm]);

  // Cache utilities with trigger-based invalidation
  const generateCacheKey = useCallback((page, limit, search) => {
    return `${apiEndpoint}_${page}_${limit}_${search || 'none'}`;
  }, [apiEndpoint]);

  const clearCacheForEndpoint = useCallback(() => {
    // Clear all cache entries for this endpoint when trigger changes
    const keysToDelete = [];
    for (const key of apiCache.keys()) {
      if (key.startsWith(`${apiEndpoint}_`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => apiCache.delete(key));
  }, [apiEndpoint]);

  const cleanupCache = useCallback(() => {
    if (apiCache.size > MAX_CACHE_SIZE) {
      const entries = Array.from(apiCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toDelete = entries.slice(0, Math.floor(MAX_CACHE_SIZE / 2));
      toDelete.forEach(([key]) => apiCache.delete(key));
    }
  }, []);

  // Optimized data fetching with request deduplication and validation
  const fetchData = useCallback(async (page = 1, limit = defaultPageSize, search = '') => {
    // Validate page bounds
    if (page < 1) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Invalid page number:', page, '- must be >= 1');
      }
      return;
    }

    // If we know totalPages from previous requests, validate against it
    if (totalPagesRef.current > 1 && page > totalPagesRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Page exceeds total pages:', page, 'of', totalPagesRef.current);
      }
      return;
    }

    // Record the latest requested page immediately (synchronously).
    // This lets goToPage rapid-click detection work before any await.
    const requestKey = `${page}_${limit}_${search}`;
    pendingPageRef.current = requestKey;

    // Only abort the previous request if it's for DIFFERENT params.
    // This prevents the initial load from cancelling itself when fetchData
    // is recreated by useCallback on the first render cycle.
    if (abortControllerRef.current && lastRequestRef.current !== requestKey) {
      abortControllerRef.current.abort();
    }
    lastRequestRef.current = requestKey;
    abortControllerRef.current = new AbortController();
    const thisController = abortControllerRef.current;

    // Check cache first (synchronous path — no loading flash)
    const cacheKey = generateCacheKey(page, limit, search);
    const cachedData = apiCache.get(cacheKey);

    if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION) {
      if (!mountedRef.current) return;
      // Only apply if this is still the latest request
      if (pendingPageRef.current !== `${page}_${limit}_${search}`) return;

      setData(cachedData.data.data || []);
      setPaginationState(prev => ({
        ...prev,
        currentPage: cachedData.data.pagination?.currentPage || page,
        totalPages: cachedData.data.pagination?.totalPages || 1,
        totalRecords: cachedData.data.pagination?.total || cachedData.data.data?.length || 0,
        isLoading: false,
        error: null
      }));
      return;
    }

    // Set loading state
    isFetchingRef.current = true;
    setPaginationState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search.trim() && { search: search.trim() })
      });

      // apiEndpoint may already contain query params (e.g. ?project_id=...)
      // Use & to append instead of ? to avoid malformed URLs
      const separator = apiEndpoint.includes('?') ? '&' : '?';
      const response = await nodeinstance.get(`${apiEndpoint}${separator}${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: thisController.signal,
      });

      if (!mountedRef.current) return;

      // Discard stale response — a newer request has already been dispatched
      if (pendingPageRef.current !== `${page}_${limit}_${search}`) return;

      if (response.data) {
        // Cache the response
        apiCache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now()
        });
        cleanupCache();

        const responseData = response.data.data || [];
        setData(responseData);

        if (response.data.pagination) {
          setPaginationState(prev => ({
            ...prev,
            currentPage: response.data.pagination.currentPage || page,
            totalPages: response.data.pagination.totalPages || 1,
            totalRecords: response.data.pagination.total || 0,
            isLoading: false,
            error: null
          }));
        } else {
          // Client-side fallback (no pagination key in response)
          const totalRecords = responseData.length;
          setPaginationState(prev => ({
            ...prev,
            currentPage: page,
            totalPages: Math.ceil(totalRecords / limit) || 1,
            totalRecords,
            isLoading: false,
            error: null
          }));
        }
      }
    } catch (error) {
      // AbortError is expected when a newer request cancels this one — ignore silently
      if (!mountedRef.current || error.name === 'AbortError') return;

      console.error("API Error:", error);
      setPaginationState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.message || error.message || "Failed to fetch data"
      }));
    } finally {
      isFetchingRef.current = false;
    }
  }, [apiEndpoint, token, defaultPageSize, generateCacheKey, cleanupCache]);

  // FIXED: Trigger-based cache invalidation effect - only runs when trigger actually changes
  useEffect(() => {
    // Only run when trigger actually changes (not on every render)
    if (trigger !== null && trigger !== prevTriggerRef.current) {
      prevTriggerRef.current = trigger;
      clearCacheForEndpoint();
      // Use refs to get current values without adding dependencies
      fetchData(currentPageRef.current, currentPageSizeRef.current, searchTermRef.current);
    }
  }, [trigger, clearCacheForEndpoint, fetchData]);

  // Fetch when apiEndpoint (filters) change
  useEffect(() => {
    if (initialLoadRef.current && apiEndpoint !== prevApiEndpointRef.current) {
      prevApiEndpointRef.current = apiEndpoint;
      if (!skipInitialLoad) {
        setPaginationState(prev => ({ ...prev, currentPage: 1 }));
        fetchData(1, currentPageSizeRef.current, searchTermRef.current);
      }
    }
  }, [apiEndpoint, fetchData, skipInitialLoad]);

  // Debounced search effect with minimal dependencies
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        // Reset to page 1 for new search
        setPaginationState(prev => ({ ...prev, currentPage: 1 }));
        fetchData(1, currentPageSizeRef.current, searchTerm);
      } else if (initialLoadRef.current && !skipInitialLoad) {
        // Only fetch if initial load is done and we are not skipping
        fetchData(currentPageRef.current, currentPageSizeRef.current, '');
      }
    }, searchDebounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchDebounceMs, fetchData, skipInitialLoad]);

  // Initial load effect - fires exactly once
  const fetchDataRef = useRef(fetchData);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      if (!skipInitialLoad) {
        fetchDataRef.current(1, defaultPageSize, '');
      }
    }
  }, [skipInitialLoad, defaultPageSize]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      initialLoadRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Navigation functions with enhanced validation
  const goToPage = useCallback((page) => {
    if (page < 1) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Cannot navigate to page', page, '- page must be >= 1');
      }
      return;
    }

    if (totalPagesRef.current > 0 && page > totalPagesRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Cannot navigate to page', page, '- exceeds total pages:', totalPagesRef.current);
      }
      return;
    }

    // Use pendingPageRef for instant dedup — currentPageRef lags behind setState
    const pendingKey = `${page}_${currentPageSizeRef.current}_${searchTermRef.current}`;
    if (pendingPageRef.current === pendingKey) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Already navigating to page', page, '- skipping duplicate');
      }
      return;
    }

    setPaginationState(prev => ({ ...prev, currentPage: page }));
    fetchData(page, currentPageSizeRef.current, searchTermRef.current);
  }, [fetchData]);

  const changePageSize = useCallback((newPageSize) => {
    // Prevent changes during loading to avoid race conditions
    if (paginationState.isLoading) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Cannot change page size while loading - request ignored');
      }
      return;
    }
    
    if (newPageSize < 1 || newPageSize > 1000) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Invalid page size:', newPageSize, '- must be between 1 and 1000');
      }
      return;
    }
    
    if (newPageSize === currentPageSizeRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Page size unchanged:', newPageSize);
      }
      return;
    }
    
    setPaginationState(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
    fetchData(1, newPageSize, searchTermRef.current);
  }, [fetchData]);

  const refresh = useCallback(() => {
    const cacheKey = generateCacheKey(currentPageRef.current, currentPageSizeRef.current, searchTermRef.current);
    apiCache.delete(cacheKey);
    fetchData(currentPageRef.current, currentPageSizeRef.current, searchTermRef.current);
  }, [generateCacheKey, fetchData]);

  return {
    data,
    paginationState,
    goToPage,
    changePageSize,
    refresh
  };
};

export default useServerPagination;